import { useCallback, useState } from "react";
import {
  CampaignServiceError,
  createProgram,
  updateProgram,
} from "../api";
import { ProgramDispatchAction } from "../types";

interface UseProgramDispatcherResult {
  dispatch: (action: ProgramDispatchAction) => Promise<void>;
  saving: boolean;
  error: string | null;
}

export function useProgramDispatcher(): UseProgramDispatcherResult {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useCallback(
    async (action: ProgramDispatchAction): Promise<void> => {
      setSaving(true);
      setError(null);

      try {
        switch (action.type) {
          case "program/create": {
            await createProgram(
              action.payload.campaignId,
              action.payload.payload,
            );

            action.payload.onSuccess?.();
            return;
          }

          case "program/update": {
            await updateProgram(
              action.payload.programId,
              action.payload.payload,
            );

            action.payload.onSuccess?.();
            return;
          }

          default: {
            const exhaustiveCheck: never = action;
            throw new Error(
              `Unsupported program action: ${JSON.stringify(exhaustiveCheck)}`,
            );
          }
        }
      } catch (dispatchError) {
        const message =
          dispatchError instanceof CampaignServiceError ||
          dispatchError instanceof Error
            ? dispatchError.message
            : "Failed to process program action.";

        setError(message);
        throw dispatchError;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  return {
    dispatch,
    saving,
    error,
  };
}
